import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Visibility, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';
import PaginatedTable from '../components/PaginatedTable';

const CreditNotesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creditNotes, setCreditNotes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadCreditNotes();
  }, [page, rowsPerPage, searchQuery]);

  const loadCreditNotes = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockCreditNotes = [
        { id: 1, creditNoteNumber: 'CN-2026-001', client: 'Atlas Construction Co', date: '2026-06-20', amount: 1500, reason: 'Damaged goods', status: 'Processed' },
        { id: 2, creditNoteNumber: 'CN-2026-002', client: 'Meridian Logistics', date: '2026-06-22', amount: 850, reason: 'Invoice correction', status: 'Pending' },
        { id: 3, creditNoteNumber: 'CN-2026-003', client: 'Orchard Foods Ltd', date: '2026-06-25', amount: 2100, reason: 'Return', status: 'Processed' },
      ];
      setCreditNotes(mockCreditNotes);
      setTotalCount(mockCreditNotes.length);
      setLoading(false);
    }, 500);
  };

  const columns = [
    { id: 'creditNoteNumber', label: t('credit_note_number'), minWidth: 150 },
    { id: 'client', label: t('client'), minWidth: 200 },
    { id: 'date', label: t('date'), minWidth: 120 },
    { id: 'amount', label: t('amount'), minWidth: 120, align: 'right' },
    { id: 'reason', label: t('reason'), minWidth: 150 },
    { id: 'status', label: t('status'), minWidth: 100 },
    { id: 'actions', label: t('actions'), minWidth: 80, align: 'center' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processed':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      case 'Pending':
        return { bg: '#FFF3E0', color: '#FF9800' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  const renderRow = (creditNote) => {
    const statusColors = getStatusColor(creditNote.status);
    return (
      <TableRow hover key={creditNote.id}>
        <TableCell sx={{ fontWeight: 500 }}>{creditNote.creditNoteNumber}</TableCell>
        <TableCell>{creditNote.client}</TableCell>
        <TableCell>{creditNote.date}</TableCell>
        <TableCell align="right">€{creditNote.amount.toLocaleString()}</TableCell>
        <TableCell>{creditNote.reason}</TableCell>
        <TableCell>
          <Chip
            label={creditNote.status}
            size="small"
            sx={{
              backgroundColor: statusColors.bg,
              color: statusColors.color,
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" color="primary">
            <Visibility fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <AccountantLayout>
      <Box>
        <Box className="accountant-page-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('credit_notes')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('manage_credit_notes')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
              },
            }}
          >
            {t('new_credit_note')}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('search_credit_notes_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <PaginatedTable
          columns={columns}
          data={creditNotes}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage={t('no_credit_notes_found')}
          renderRow={renderRow}
        />
      </Box>
    </AccountantLayout>
  );
};

export default CreditNotesPage;
